import { useCallback, useContext, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { UserContext } from 'providers/userProvider';
import { startSession } from 'lib/auth/session';
import type { TUser } from 'containers/app/trainer/models';

import { itemOf, messageOf, SOMETHING_WRONG } from './api';
import type { TAnswer, TSignInResult } from './api';
import { SIGN_IN_PATHS } from './constants';
import { updateFlow } from './flow';

const NOT_FOR_PORTAL = 'This account can’t use the coach portal.';

/** False once the screen has gone, so a late answer doesn't set state on it. */
export const useLive = () => {
  const live = useRef(true);
  useEffect(() => () => {
    live.current = false;
  }, []);
  return live;
};

/**
 * Acts on a sign-in style answer (sign in, code, reset, invite): signs in and
 * goes to the welcome screen, or moves to the code / client / paused / expired
 * screen. Returns the message for the red box when the answer stays on this
 * screen, or null when it moved on.
 */
export const useOutcome = () => {
  const history = useHistory();
  const { setUser } = useContext(UserContext);

  return useCallback(async (answer: TAnswer<TSignInResult>, context: { keepSignedIn: boolean; email?: string | null }): Promise<string | null> => {
    const result = itemOf(answer);
    if (!result) {
      return messageOf(answer);
    }
    switch (result.outcome) {
      case 'signed-in': {
        if (!result.roles?.some((role) => role === 'Trainers' || role === 'SubCoaches')) {
          return NOT_FOR_PORTAL;
        }
        const me = itemOf<TUser>(await startSession(result, { keepSignedIn: context.keepSignedIn }));
        if (!me) {
          return SOMETHING_WRONG;
        }
        updateFlow({ challengeId: null, codeSentTo: null, codeSentAt: null, notice: null });
        setUser({ ...me, roles: result.roles, isTemporaryPasswordChanged: result.isTemporaryPasswordChanged });
        history.push(SIGN_IN_PATHS.WELCOME);
        return null;
      }
      case 'code-required':
        updateFlow({
          email: context.email || undefined,
          keepSignedIn: context.keepSignedIn,
          challengeId: result.challengeId,
          codeSentTo: result.codeSentTo,
          codeSentAt: Date.now(),
        });
        history.push(SIGN_IN_PATHS.CODE);
        return null;
      case 'client-account':
        updateFlow({ email: result.email || context.email || undefined });
        history.push(SIGN_IN_PATHS.CLIENT);
        return null;
      case 'account-paused':
        updateFlow({ coachName: result.coachName, isTeamMember: !!result.isTeamMember });
        history.push(SIGN_IN_PATHS.PAUSED);
        return null;
      case 'link-expired':
        history.push(SIGN_IN_PATHS.EXPIRED);
        return null;
      default:
        return result.message || SOMETHING_WRONG;
    }
  }, [history, setUser]);
};
